import React, { useEffect, useState } from 'react';
import { soundManager } from '../utils/soundUtils';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import popupBg from '../assets/images/popup-bg.png';
import endingPopOk from '../assets/images/ending-pop-ok.png';
import { doc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { findValidUser, getUserFromTimeBasedCollection } from '../utils/firebaseUtils';

// 배후성 리스트 (가입 페이지의 배후성 데이터에서 추출)
const BACKGROUND_STORIES = [
  "구원의 마왕",
  "심연의 흑염룡",
  "올림푸스",  
  "가장 어두운 봄의 여왕",
  "해상전신",
  "강철의 주인",
  "악마같은 불의 심판자",
  "무저갱의 지배자",
  "디펜스 마스터",
  "구암신의",
  "버려진 미로의 연인",
  "술과 황홀경의 신",
  "은밀한 모략가",
  "긴고아의 죄수",
  "하늘의 서기관",
  "고려제일검",
  "부유한 밤의 아버지",
  "매금지존",
  "오공원 두꺼비",
  "시조의 어머니",
  "이천일류의 달인",
  "절름발이 사기꾼",
  "여덟 머리의 군주",
  "잡배의 군주",
  "넉살 좋은 잡담꾼",
  "늙은 시계추의 관리자",
  "저주받은 검투사",
  "장막 뒤의 그림자",
  "외눈 미륵",
  "조선제일술사",
  "헌천홍도경문위무대왕",
  "양다리 전문가",
  "국정농단의 달인"
];

// 메시지 리스트 (13MessageSend.tsx에서 가져온 실제 메시지)
const MESSAGES = [
  '당신의 선택에 흥미로워합니다.',
  '당신의 선택에 재미있어합니다.',
  '당신의 호쾌한 발언에\n고개를 끄덕입니다.',
  '당신의 선택에 고개를 끄덕입니다.',
  '당신의 계획에 눈을 반짝입니다.',
  '당신의 계책을 궁금해합니다.',
  '당신의 근성에 감탄합니다.',
  '당신의 침착함에 감탄합니다.',
  '알고 있었다는 듯\n흐뭇한 미소를 짓습니다.',
  '당신의 준비성에 감탄합니다.',
  '즐거워합니다.',
  '당신의 혼잣말을 좋아합니다.',
  '눈을 반짝입니다.',
  '당신의 선택을 기대합니다.',
  '감동하며 눈물을 글썽입니다.',
  '아쉬워합니다.',
  '기뻐합니다.',
  '당신의 준비성을 칭찬합니다.',
  '그럴줄 알았다며 감탄합니다.'
];

const PopupContainer = styled.div`
  width: 90vw;
  max-width: 85vw;
  aspect-ratio: 1.55 / 1;
  position: relative;
  margin: 0 auto;
  z-index: 10;
  background: none;
  overflow: hidden;
`;

const PopupBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${popupBg});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  opacity: 1;
  z-index: 1;
`;

const PopupText = styled.div`
  width: 85%;
  position: absolute;
  top: 53%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  font-family: 'MapoDPP';
  color: #fff;
  text-align: center;
  display: block;
  white-space: pre-line;
  text-shadow: 0 0px 0px rgba(255, 255, 255, 0.01), 0 0 10px #fff;
  font-size: 4.2dvw;
  line-height: 1.4;
`;

const ConfirmButton = styled.img`
  max-width: 80dvw;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

interface PeriodicMessagePopupProps {
  isVisible: boolean;
  onClose: () => void;
}

const PeriodicMessagePopup: React.FC<PeriodicMessagePopupProps> = ({ isVisible, onClose }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedBackground, setSelectedBackground] = useState('');
  const [selectedMessage, setSelectedMessage] = useState('');
  const location = useLocation();

  // 팝업이 비활성화되어야 하는 페이지들
  const disabledPages = ['/', '/guide01', '/register', '/contract', '/url-generator', '/analytics'];
  const isPopupDisabled = disabledPages.includes(location.pathname);

  // 메시지를 파이어베이스에 저장하는 함수
  const saveMessageToFirebase = async () => {
    try {
      // 현재 사용자 정보 가져오기
      const currentUser = localStorage.getItem('currentUser');
      if (!currentUser) return;
      
      const userData = JSON.parse(currentUser);
      if (!userData?.nickname || !userData?.avatarNumber) return;
      
      // 새로운 시간 기반 컬렉션 구조를 사용하여 사용자 정보 가져오기
      const userInfo = await getUserFromTimeBasedCollection(userData.nickname, userData.avatarNumber);
      
      if (userInfo) {
        const { collectionName, docId } = userInfo;
        
        // 받은 메시지 생성
        const newMessage = {
          message: selectedMessage,
          senderNickname: selectedBackground,
          receivedAt: Timestamp.now(),
          isSystemMessage: true
        };
        
        // receivedMessages 배열에 메시지 추가
        const userDocRef = doc(db, collectionName, docId);
        await updateDoc(userDocRef, {
          receivedMessages: arrayUnion(newMessage)
        });
        
      }
    } catch (error) {
      console.error('메시지 저장 중 오류 발생:', error);
    }
  };

  useEffect(() => {
    if (isVisible) {
      // 비활성화된 페이지에서는 팝업을 표시하지 않음
      if (isPopupDisabled) {
        onClose();
        return;
      }

      // 팝업 표시 횟수 확인 (최대 2개까지 표시)
      const popupCount = parseInt(localStorage.getItem('periodicPopupCount') || '0');
      
      
      // 2개 이상 표시되었으면 팝업을 표시하지 않음
      if (popupCount >= 2) {
        onClose();
        return;
      }
      
      // 랜덤으로 배후성과 메시지 선택
      const randomBackground = BACKGROUND_STORIES[Math.floor(Math.random() * BACKGROUND_STORIES.length)];
      const randomMessage = MESSAGES[Math.floor(Math.random() * MESSAGES.length)];
      
      // 선택된 값들을 state에 저장
      setSelectedBackground(randomBackground);
      setSelectedMessage(randomMessage);
      
      const fullMessage = `[성좌, '${randomBackground}'이(가) \n${randomMessage} ]`;
      setMessage(fullMessage);
      setShowPopup(true);
      
    }
  }, [isVisible, onClose, isPopupDisabled, location.pathname]);

  const handleConfirmClick = async () => {
    setShowPopup(false);
    
    // 비활성화된 페이지에서는 메시지 저장과 카운트 증가를 하지 않음
    if (isPopupDisabled) {
      onClose();
      return;
    }
    
    // 메시지를 파이어베이스에 저장
    await saveMessageToFirebase();
    
    // 팝업 표시 횟수 증가
    const currentCount = parseInt(localStorage.getItem('periodicPopupCount') || '0');
    localStorage.setItem('periodicPopupCount', (currentCount + 1).toString());
    
    onClose();
  };

  if (!showPopup) return null;

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100dvw', 
      height: '100dvh', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      zIndex: 1000,
      background: 'rgba(0, 0, 0, 0.8)'
    }}>
      <PopupContainer>
        <PopupBg />
        <PopupText>
          {message}
        </PopupText>
      </PopupContainer>
      <div style={{ 
        marginTop: '3dvh',
        display: 'flex', 
        justifyContent: 'center', 
        zIndex: 3 
      }}>
        <ConfirmButton 
          src={endingPopOk} 
          alt="확인" 
          onClick={handleConfirmClick}
        />
      </div>
    </div>
  );
};

export default PeriodicMessagePopup;
